import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Check, Copy, Link2, Send } from 'lucide-react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { getErrorMessage } from '../../../lib/apiClient';
import { formatDateNumeric } from '../../../utils/format';
import { publishDocument } from '../signaturesApi';
import { fieldType, signerColor } from '../lib/fieldTypes';

/**
 * Wizard step 3 — review recipients/fields, customize the invitation email,
 * and publish. Agreements email each recipient their link; waivers mint the
 * shareable public URL, shown here to copy.
 */
export default function StepReview({ document: doc, signers, fields, onPublished }) {
  const isAgreement = doc.type === 'agreement';
  const [subject, setSubject] = useState(`Signature requested: ${doc.title}`);
  const [message, setMessage] = useState('');
  const [autoReminders, setAutoReminders] = useState(true);
  const [waiverUrl, setWaiverUrl] = useState(null);
  const [copied, setCopied] = useState(false);

  const publish = useMutation({
    mutationFn: () =>
      publishDocument({
        id: doc.id,
        emailData: isAgreement ? { subject, message: message || undefined } : undefined,
        autoReminders: isAgreement ? autoReminders : undefined,
      }),
    onSuccess: (result) => {
      if (result.url) {
        setWaiverUrl(result.url);
        toast.success('Document published — share the link below');
      } else {
        toast.success('Signature requests sent');
        onPublished(result.document);
      }
    },
    onError: (err) => toast.error(getErrorMessage(err)),
  });

  const copyUrl = async () => {
    await navigator.clipboard.writeText(waiverUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const fieldCountBySigner = (id) => fields.filter((f) => f.signerId === id).length;

  if (waiverUrl) {
    return (
      <div className="mx-auto max-w-xl space-y-4 rounded-2xl border border-slate-200 bg-slate-100 p-8 text-center">
        <Link2 className="mx-auto h-10 w-10 text-brand-600" />
        <h2 className="text-lg font-semibold text-slate-900">Your document is live</h2>
        <p className="text-sm text-slate-500">
          Anyone with this link can sign — each person gets their own signed copy.
        </p>
        <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
          <span className="min-w-0 flex-1 truncate text-left text-sm text-slate-700">
            {waiverUrl}
          </span>
          <Button variant="secondary" size="sm" onClick={copyUrl}>
            {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
            {copied ? 'Copied' : 'Copy'}
          </Button>
        </div>
        <Button onClick={() => onPublished(doc)}>Go to document</Button>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <div className="rounded-2xl border border-slate-200 bg-slate-100 p-6">
        <h2 className="mb-1 text-base font-semibold text-slate-900">{doc.title}</h2>
        <p className="text-sm text-slate-500">
          {doc.pagesInPdf} page{doc.pagesInPdf === 1 ? '' : 's'} · {fields.length} field
          {fields.length === 1 ? '' : 's'} placed
          {doc.expiresAt && ` · expires ${formatDateNumeric(doc.expiresAt)}`}
        </p>

        {isAgreement ? (
          <ul className="mt-4 space-y-2">
            {signers.map((signer) => (
              <li
                key={signer.id}
                className="flex items-center gap-3 rounded-lg border border-slate-100 bg-slate-50 px-3 py-2"
              >
                <span
                  className="h-3 w-3 shrink-0 rounded-full"
                  style={{ backgroundColor: signerColor(signer.sequence) }}
                />
                <span className="min-w-0 flex-1 truncate text-sm text-slate-700">
                  {signer.firstName} {signer.lastName}
                  <span className="text-slate-400"> · {signer.email}</span>
                </span>
                <span className="text-xs text-slate-400">
                  {fieldCountBySigner(signer.id)} field{fieldCountBySigner(signer.id) === 1 ? '' : 's'}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <ul className="mt-4 flex flex-wrap gap-1.5">
            {fields.map((f) => (
              <li
                key={f.id}
                className="rounded-full bg-slate-200 px-2.5 py-1 text-xs text-slate-600"
              >
                {f.label || fieldType(f.kind)?.label || f.kind}
              </li>
            ))}
          </ul>
        )}
      </div>

      {isAgreement && (
        <div className="space-y-3 rounded-2xl border border-slate-200 bg-slate-100 p-6">
          <h3 className="text-sm font-semibold text-slate-800">Invitation email</h3>
          <Input
            label="Subject"
            value={subject}
            maxLength={255}
            onChange={(e) => setSubject(e.target.value)}
          />
          <div className="space-y-1">
            <label className="block text-sm font-medium text-slate-700">Message (optional)</label>
            <textarea
              value={message}
              maxLength={2000}
              rows={4}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Add a personal note for your recipients…"
              className="block w-full rounded-lg border border-slate-200 bg-slate-100 px-4 py-2.5 text-sm text-slate-800 placeholder:text-slate-400 focus:border-brand-600 focus:outline-none"
            />
          </div>
          <label className="flex items-start gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={autoReminders}
              onChange={(e) => setAutoReminders(e.target.checked)}
            />
            <span>
              Send automatic reminders to anyone who hasn&apos;t signed, 3 and 7 days after sending
            </span>
          </label>
        </div>
      )}

      <div className="flex justify-end">
        <Button loading={publish.isPending} onClick={() => publish.mutate()}>
          <Send className="h-4 w-4" />
          {isAgreement ? 'Publish & send invitations' : 'Publish document'}
        </Button>
      </div>
    </div>
  );
}
